import { INPUT_TYPES } from 'app-constants/inputTypes';
import { ACTION_TYPES } from 'app-constants/actionTypes';
import { getActionAnswerType } from './nodes';
import slugify from './utils';

const radio = (transitionId) => ({
  id: {
    reference: `transition.${transitionId}.output.answer.selected.id`,
    type: 'string',
  },
  label: {
    reference: `transition.${transitionId}.output.answer.selected.label`,
    type: 'string',
  },
});

const checkbox = (transitionId) => ({
  ids: {
    reference: `transition.${transitionId}.output.answer.ids`,
    type: 'array',
  },
  labels: {
    reference: `transition.${transitionId}.output.answer.labels`,
    type: 'array',
  },
});

const location = (transitionId) => ({
  latitude: {
    reference: `transition.${transitionId}.output.answer.latitude`,
    type: 'number',
  },
  longitude: {
    reference: `transition.${transitionId}.output.answer.longitude`,
    type: 'number',
  },
  timestamp: {
    reference: `transition.${transitionId}.output.answer.timestamp`,
    type: 'number',
  },
  accuracy: {
    reference: `transition.${transitionId}.output.answer.accuracy`,
    type: 'number',
  },
});

const media = (transitionId) => ({
  media: {
    reference: `transition.${transitionId}.output.answer`,
    type: 'object',
  },
  location: {
    reference: `transition.${transitionId}.output.location`,
    type: 'object',
  },
  single: {
    media_type: {
      reference: `transition.${transitionId}.output.answer.media_type`,
      type: 'string',
    },
    url: {
      reference: `transition.${transitionId}.output.answer.url`,
      type: 'string',
    },
    latitude: {
      reference: `transition.${transitionId}.output.location.latitude`,
      type: 'number',
    },
    longitude: {
      reference: `transition.${transitionId}.output.location.longitude`,
      type: 'number',
    },
    timestamp: {
      reference: `transition.${transitionId}.output.location.timestamp`,
      type: 'number',
    },
    accuracy: {
      reference: `transition.${transitionId}.output.location.accuracy`,
      type: 'number',
    },
  },
});

const barcode = (transitionId) => ({
  barcode: {
    reference: `transition.${transitionId}.output.answer`,
    type: 'object',
  },
  location: {
    reference: `transition.${transitionId}.output.location`,
    type: 'object',
  },
  single: {
    format: {
      reference: `transition.${transitionId}.output.answer.format`,
      type: 'string',
    },
    raw: {
      reference: `transition.${transitionId}.output.answer.raw`,
      type: 'string',
    },
    type: {
      reference: `transition.${transitionId}.output.answer.type`,
      type: 'string',
    },
    value: {
      reference: `transition.${transitionId}.output.answer.value`,
      type: 'string',
    },
    latitude: {
      reference: `transition.${transitionId}.output.location.latitude`,
      type: 'number',
    },
    longitude: {
      reference: `transition.${transitionId}.output.location.longitude`,
      type: 'number',
    },
    timestamp: {
      reference: `transition.${transitionId}.output.location.timestamp`,
      type: 'number',
    },
    accuracy: {
      reference: `transition.${transitionId}.output.location.accuracy`,
      type: 'number',
    },
  },
});

const signature = (transitionId) => ({
  signature: {
    reference: `transition.${transitionId}.output.answer`,
    type: 'object',
  },
  location: {
    reference: `transition.${transitionId}.output.location`,
    type: 'object',
  },
  single: {
    media_type: {
      reference: `transition.${transitionId}.output.answer.media_type`,
      type: 'string',
    },
    url: {
      reference: `transition.${transitionId}.output.answer.url`,
      type: 'string',
    },
    latitude: {
      reference: `transition.${transitionId}.output.location.latitude`,
      type: 'number',
    },
    longitude: {
      reference: `transition.${transitionId}.output.location.longitude`,
      type: 'number',
    },
    timestamp: {
      reference: `transition.${transitionId}.output.location.timestamp`,
      type: 'number',
    },
    accuracy: {
      reference: `transition.${transitionId}.output.location.accuracy`,
      type: 'number',
    },
  },
});

const numberOutput = (transitionId) => ({
  simple: {
    reference: `transition.${transitionId}.output.answer.value`,
    type: 'number',
  },
});

const dateOutput = (transitionId) => ({
  format: {
    reference: `transition.${transitionId}.output.answer.format`,
    type: 'string',
  },
  selected_date: {
    reference: `transition.${transitionId}.output.answer.selected_date`,
    type: 'string',
  },
});

const simpleOutput = (transitionId) => ({
  simple: {
    reference: `transition.${transitionId}.output.answer.value`,
    type: 'string',
  },
});

const getSendMessageResultReference = (transitionId, input) => {
  const inputTypeReference = {
    [INPUT_TYPES.TEXT]: simpleOutput,
    [INPUT_TYPES.NUMBER]: numberOutput,
    [INPUT_TYPES.PHONE]: simpleOutput,
    [INPUT_TYPES.EMAIL]: simpleOutput,
    [INPUT_TYPES.RADIO]: radio,
    [INPUT_TYPES.CHECKBOX]: checkbox,
    [INPUT_TYPES.DATE]: dateOutput,
    [INPUT_TYPES.PASSWORD]: simpleOutput,
    [INPUT_TYPES.LOCATION]: location,
    [INPUT_TYPES.MEDIA]: media,
    [INPUT_TYPES.BARCODE]: barcode,
    [INPUT_TYPES.SIGNATURE]: signature,
  };
  if (!input.type) {
    return {};
  }
  return inputTypeReference[input.type](transitionId);
};

const getCallApiResultReference = (...[transitionId, , properties]) => {
  const answerType = getActionAnswerType(properties);
  return {
    status: {
      reference: `transition.${transitionId}.output.status`,
      type: 'number',
    },
    response_time: {
      reference: `transition.${transitionId}.output.response_time`,
      type: 'number',
    },
    answer: {
      reference: `transition.${transitionId}.output.answer`,
      ...answerType && { type: answerType },
    },
  };
};

const getCallFunctionResultReference = (...[transitionId, , properties]) => {
  const answerType = getActionAnswerType(properties);
  return {
    simple: {
      reference: `transition.${transitionId}.output.answer`,
      ...answerType && { type: answerType },
    },
  };
};

const getResultReference = (transitionId) => ({
  simple: {
    reference: `transition.${transitionId}.output.answer`,
  },
});

const getResultReferenceType = (transitionId) => ({
  simple: {
    reference: `transition.${transitionId}.output.answer`,
    type: 'string',
  },
});

const calculateActionOutput = (actionType, transitionId, input, properties) => {
  const getActionReference = {
    [ACTION_TYPES.SEND_MESSAGE]: getSendMessageResultReference,
    [ACTION_TYPES.CALL_API]: getCallApiResultReference,
    [ACTION_TYPES.CALL_FUNCTION]: getCallFunctionResultReference,
    [ACTION_TYPES.EVAL_NEXT]: getResultReference,
    [ACTION_TYPES.STORE_DATA]: getResultReference,
    [ACTION_TYPES.CONDITIONAL_STEP]: getResultReferenceType,
    [ACTION_TYPES.STORE_FILE]: getResultReferenceType,
  };
  return getActionReference[slugify(actionType)](transitionId, input, properties);
};

export default calculateActionOutput;
